import * as React from 'react'
import { ReactElement } from 'react'
import {
    ButtonProps,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Overflow,
    OverflowItem,
    OverflowItemProps,
    Tab,
    TabList,
    TabProps,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components'
import type { ObserveOptions } from '@fluentui/priority-overflow'
import { useTranslation } from 'react-i18next'
import { SelectTabEventHandler, TabListProps } from '@fluentui/react-tabs'
import { bundleIcon, MoreVerticalFilled, MoreVerticalRegular } from '@fluentui/react-icons'

const MoreIcon = bundleIcon(MoreVerticalFilled, MoreVerticalRegular)

export type HammockTabListProps = Pick<TabListProps, 'appearance' | 'className' | 'selectTabOnFocus' | 'size'>
export type HammockTabProps = Pick<TabProps, 'className'>
export type HammockButtonProps = Pick<ButtonProps, 'appearance' | 'className' | 'size'>

export type HammockTabData = {
    titleKey: string
    props: Omit<TabProps, 'onClick'>
}

type OverflowTabListPanel = Partial<ObserveOptions> & {
    tabs: HammockTabData[]
    onTabSelect: SelectTabEventHandler
    selectedValue: string
    overflowButtonLabelKey: string

    containerClassName?: string
    tabListProps?: HammockTabListProps
    tabProps?: HammockTabProps
}

type OverflowMenuItemProps = OverflowTabListPanel &
    Pick<OverflowItemProps, 'id'> & {
        value: HammockTabData
    }

function OverflowMenuItem(props: OverflowMenuItemProps): ReactElement {
    const { t: translate } = useTranslation()
    const isVisible = useIsOverflowItemVisible(props.id)

    if (isVisible) {
        return null
    }

    return (
        <MenuItem
            onClick={() => {
                props.onTabSelect(undefined, props.value.props)
            }}
            icon={props.value.props.icon}
        >
            {translate(props.value.titleKey)}
        </MenuItem>
    )
}

function OverflowMenu(props: OverflowTabListPanel): ReactElement {
    const { t: translate } = useTranslation()
    const { ref, overflowCount, isOverflowing } = useOverflowMenu<HTMLButtonElement>()

    if (!isOverflowing) {
        return null
    }

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={`+${translate(props.overflowButtonLabelKey, { count: overflowCount })}`}>
                    <Tab tabIndex={-1} ref={ref} {...props.tabProps} value={'more'}>
                        <MoreIcon />
                    </Tab>
                </Tooltip>
            </MenuTrigger>

            <MenuPopover>
                <MenuList>
                    {props.tabs?.map((value, index) => (
                        <OverflowMenuItem key={index} id={`${index}`} value={value} {...props} />
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    )
}

export default function OverflowTabListPanel(props: OverflowTabListPanel): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Overflow minimumVisible={props.minimumVisible ?? 3}>
            <div className={props.containerClassName}>
                <TabList {...props.tabListProps} onTabSelect={props.onTabSelect} selectedValue={props.selectedValue}>
                    {props.tabs?.map((tab, index) => (
                        <OverflowItem key={index} id={`${index}`}>
                            <Tooltip key={index} relationship={'label'} content={translate(tab.titleKey)}>
                                <Tab key={index} {...tab.props} {...props.tabProps} />
                            </Tooltip>
                        </OverflowItem>
                    ))}
                    <OverflowMenu {...props} />
                </TabList>
            </div>
        </Overflow>
    )
}
